import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

require('./ClickToCopy.css');

export type Props = {
    className?: string;
    children?: ReactNode;
};

export const ClickToCopy = ({ className, children }: Props) => {
    const { t } = useTranslation();

    const hostRef = useRef<HTMLDivElement | null>(null);
    const timerId1Ref = useRef<NodeJS.Timer>();
    const timerId2Ref = useRef<NodeJS.Timer>();

    const [ clicked, setClicked ] = useState(false);
    const [ copied, setCopied ] = useState(false);

    // Clear timers when we unmount
    useEffect(() => {
        clearTimeout(timerId1Ref.current!);
        clearTimeout(timerId2Ref.current!);
    }, [ timerId1Ref, timerId2Ref ]);

    const selectAndCopy = useCallback(() => {
        if (!document.queryCommandSupported('copy')) {
            console.error('Failed to copy - not supported');
            return;
        }

        // Select the content
        const selection = window.getSelection();
        if (selection) {
            selection.selectAllChildren(hostRef.current!);

            if (!document.queryCommandEnabled('copy')) {
                console.error('Failed to copy - disabled');
            }
            if (!document.execCommand('copy')) {
                console.error('Failed to copy');
            }

            // Deselect the content
            selection.removeAllRanges();
        }
    }, [ hostRef ]);

    const handleClick = useCallback(() => {
        clearTimeout(timerId1Ref.current!);
        clearTimeout(timerId2Ref.current!);

        selectAndCopy();
        setClicked(true);
        setCopied(true);

        timerId1Ref.current = setTimeout(() => setClicked(false), 60);
        timerId2Ref.current = setTimeout(() => setCopied(false), 1000);
    }, [ timerId1Ref, timerId2Ref, selectAndCopy, setClicked, setCopied ]);

    return (
        <span
            onClick={ handleClick }
            className={ `click-to-copy ${clicked ? 'clicked' : ''} ${copied ? 'copied' : ''} ${className || ''}` }
            data-variation="mini"
            data-tooltip={ t(copied ? 'COPIED' : 'COMMAND.CLICK_TO_COPY') }
            data-position="right center"
            ref={ hostRef }
        >
            { children }
        </span>
    );
};
